// @ts-nocheck
import React, { FunctionComponent, useCallback } from 'react'
import moment from 'moment'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { colors } from 'Styled/variables'
import {
  calcTimeLeft,
  getStatusView,
  StatusTextEnum,
} from 'Containers/Tracks/helpers'
import {
  InfluencerCampaign,
  InfluencerEventStatus,
} from 'Redux/reducers/influencerReducer'
import { setID } from 'Redux/actions/influencerActions'
import track from 'Shared/img/track.png'
import {
  ArtistName,
  CampaignProgress,
  ListItemContainer,
  DateProgress,
  DetailsButton,
  Status,
  StatusWrapper,
  TrackCoverWrapper,
  TrackInfo,
  TrackName,
  Reward,
  PaidContainer,
  CampaignProgressWrapper,
} from './styles'

type TracksListProps = {
  campaign: InfluencerCampaign
  id: number
  onDetailsClick: (id: number) => void
}

export const ListItem: FunctionComponent<TracksListProps> = ({
  id,
  campaign,
  onDetailsClick,
}) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const PERIOD_TO_ACCEPT = 7
  const PERIOD_OF_UPLOAD = 3
  const { text: statusText, color } = getStatusView(campaign?.status, t)

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const startDateTz = moment.tz(campaign?.startDate, timezone)
  const createdDateTz = moment.tz(campaign?.createdAt, timezone)

  const endDateTz =
    campaign?.status === InfluencerEventStatus.ACCEPTED
      ? moment.tz(campaign?.deadlineToLoadVideo, timezone)
      : moment.tz(campaign?.deadlineToAccept, timezone)

  const campaignDaysLength = endDateTz.diff(startDateTz, 'days')
  const campaignVideoUploadDaysLength = endDateTz.diff(createdDateTz, 'days')
  const daysLeft = endDateTz.diff(moment(), 'days')
  const timeLeft = calcTimeLeft(endDateTz.format('MM/DD/YYYY'), t)
  const willBeReceivedData =
    moment.tz(campaign?.paymentWillBeReceived, timezone).format('MM/DD/YYYY') ||
    ''

  const handleDetailsClick = useCallback(() => {
    onDetailsClick(id)
    dispatch(setID(id))
  }, [id, onDetailsClick])

  const campaignProgressPercentHandler = useCallback(
    status => {
      if (
        status === StatusTextEnum.IN_PROGRESS ||
        status === StatusTextEnum.AWAITING_REVIEW
      ) {
        return (daysLeft / campaignVideoUploadDaysLength) * 100
      }

      return (daysLeft / campaignDaysLength) * 100
    },
    [daysLeft, campaignVideoUploadDaysLength, campaignDaysLength]
  )

  const stepsShowHandler = useCallback(status => {
    if (status === StatusTextEnum.NEW_TRACK) {
      return PERIOD_TO_ACCEPT
    }

    if (
      status === StatusTextEnum.IN_PROGRESS ||
      status === StatusTextEnum.AWAITING_REVIEW
    ) {
      return PERIOD_OF_UPLOAD
    }

    return 0
  }, [])

  const strokeColorHandler = useCallback((status, daysLeft) => {
    if (status === StatusTextEnum.NEW_TRACK) {
      return daysLeft >= 5 ? colors.green : colors.messegesYellow1
    }

    if (
      status === StatusTextEnum.IN_PROGRESS ||
      status === StatusTextEnum.AWAITING_REVIEW
    ) {
      return daysLeft <= 2 ? colors.messegesYellow1 : colors.green
    }

    return colors.black
  }, [])

  return (
    <ListItemContainer>
      <TrackCoverWrapper>
        <img src={campaign?.trackCoverUrl || track} alt="Track cover" />
      </TrackCoverWrapper>

      <TrackInfo status={statusText}>
        <TrackName>{campaign?.trackTitle || ''}</TrackName>
        <ArtistName>{campaign?.trackAuthorName || ''}</ArtistName>
      </TrackInfo>

      <CampaignProgress>
        {statusText !== StatusTextEnum.CANCELED && (
          <CampaignProgressWrapper>
            {endDateTz.format('MM/DD/YYYY')}
            {timeLeft && `, ${timeLeft}`}
          </CampaignProgressWrapper>
        )}
        {(statusText === StatusTextEnum.NEW_TRACK ||
          statusText === StatusTextEnum.IN_PROGRESS ||
          statusText === StatusTextEnum.AWAITING_REVIEW) && (
          <DateProgress
            percent={campaignProgressPercentHandler(statusText)}
            steps={stepsShowHandler(statusText)}
            strokeWidth={4}
            showInfo={false}
            strokeColor={strokeColorHandler(statusText, daysLeft)}
            trailColor={colors.greyTextBlackBg}
            status="active"
          />
        )}
      </CampaignProgress>
      <StatusWrapper>
        <Status $color={color} status={statusText}>
          <div>{statusText}</div>
        </Status>
        {statusText === StatusTextEnum.COMPLETE && (
          <>
            <Reward>
              {t('influencer.reward')}:&nbsp;&#36;{campaign?.reward}
              &nbsp;(
              {campaign?.stars}
              &nbsp;
              {t('influencer.rate')})
            </Reward>

            {campaign?.isPaymentReceived ? (
              <PaidContainer>{t('influencer.paid')} </PaidContainer>
            ) : (
              <PaidContainer>
                {t('influencer.willBeReceived')}&nbsp;
                {campaign?.paymentWillBeReceived && willBeReceivedData}
              </PaidContainer>
            )}
          </>
        )}
      </StatusWrapper>

      <DetailsButton color="whiteGreenNarrow" onClick={handleDetailsClick}>
        {t('influencer.details')}
      </DetailsButton>
    </ListItemContainer>
  )
}
